/**
 * useTurnstile — Cloudflare Turnstile widget loader + token state.
 *
 * Loads the Turnstile script once per page (cached across re-renders),
 * renders the widget into a callback-ref'd div, and exposes the current
 * token.
 *
 * The container ref is a CALLBACK REF (not a useRef object) so that the
 * widget renders the moment React attaches the div to the DOM, even if
 * the div is conditionally mounted later in the component tree (e.g.
 * after a slot is selected and the booking form appears). With a
 * useRef-style RefObject, useEffect ran once on mount with
 * containerRef.current === null, the script-loaded `.then` bailed on
 * the null check, and the widget never rendered when the div finally
 * appeared. Callback refs run on every mount/unmount, so we render on
 * the first mount and clean up on unmount, regardless of conditional
 * rendering. PR #43.1.
 *
 * Token lifecycle:
 *   - null when widget hasn't completed yet (loading or expired)
 *   - string when challenge has been passed
 *   - resets to null on expiration; tokens are single-use server-side
 *
 * Usage:
 *   const { containerRef, token, reset } = useTurnstile(siteKey);
 *   <div ref={containerRef} className="turnstile-widget" />
 *   // containerRef is a callback function, but `ref={...}` accepts
 *   // both callback and object refs identically.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface TurnstileOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  appearance?: 'always' | 'execute' | 'interaction-only';
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileOptions) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    __aaTurnstileLoaded?: () => void;
    __aaTurnstileReady?: Promise<void>;
  }
}

const SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__aaTurnstileLoaded';

let scriptLoadPromise: Promise<void> | null = null;

function ensureTurnstileScript(): Promise<void> {
  // Path 1: index.html eager-loaded the script and set up __aaTurnstileReady.
  if (window.__aaTurnstileReady) return window.__aaTurnstileReady;

  // Path 2: turnstile already on window (different load mechanism)
  if (window.turnstile) return Promise.resolve();

  // Path 3 (fallback for local dev without preload): inject the script ourselves.
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    window.__aaTurnstileLoaded = () => resolve();
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error('Failed to load Turnstile script'));
    document.head.appendChild(s);
  });
  return scriptLoadPromise;
}

interface UseTurnstileResult {
  /** Callback ref — pass directly to <div ref={containerRef} />. */
  containerRef: (el: HTMLDivElement | null) => void;
  token: string | null;
  /** True if the widget rendered (script loaded + render() succeeded). */
  ready: boolean;
  /** Manually reset the widget (clears token, asks Turnstile for a new challenge). */
  reset: () => void;
}

export function useTurnstile(siteKey: string | undefined): UseTurnstileResult {
  const elRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // siteKey is captured in a ref so the callback ref doesn't churn when
  // the parent re-renders — siteKey is effectively a constant from env.
  const siteKeyRef = useRef(siteKey);
  useEffect(() => {
    siteKeyRef.current = siteKey;
  }, [siteKey]);

  const containerRef = useCallback((el: HTMLDivElement | null) => {
    // Unmount path: clean up the previous widget.
    if (!el) {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // best-effort cleanup
        }
        widgetIdRef.current = null;
      }
      elRef.current = null;
      setReady(false);
      setToken(null);
      return;
    }

    // Mount path: store the element, then render once the script loads.
    elRef.current = el;
    const sk = siteKeyRef.current;
    if (!sk) {
      // Dev mode without Turnstile configured — skip render
      return;
    }
    // If we already rendered into this exact element, bail to avoid
    // duplicate widgets when React re-attaches the same node.
    if (widgetIdRef.current) return;

    ensureTurnstileScript()
      .then(() => {
        // Element may have unmounted between script load start and finish.
        const current = elRef.current;
        if (!current || current !== el) return;
        if (!window.turnstile) return;
        if (widgetIdRef.current) return; // double-render guard
        const widgetId = window.turnstile.render(current, {
          sitekey: sk,
          callback: (t) => setToken(t),
          'expired-callback': () => setToken(null),
          'error-callback': () => setToken(null),
          theme: 'light',
        });
        widgetIdRef.current = widgetId;
        setReady(true);
      })
      .catch((err) => {
        console.error('Turnstile failed to load', err);
      });
  }, []);

  function reset() {
    setToken(null);
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {
        // ignore
      }
    }
  }

  return { containerRef, token, ready, reset };
}
